import type { AuthMode, DomainEvent, Lane, QuotaLimitKind, WorkOrderState } from "@szyos/contracts";
import { orderActivity, type ActivityFacts } from "@szyos/domain";

export function time(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export function timeSec(d: Date): string {
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Zeitpunkt, ab dem etwas weitergeht: heute nur mit Uhrzeit, sonst mit Datum. */
export function untilText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const sameDay = d.toDateString() === new Date().toDateString();
  return `${sameDay ? time(d) : dateTime(iso)} Uhr`;
}

/** Ersetzt ISO-Zeitpunkte in Servertexten (z. B. `stateReason`) durch lesbare Uhrzeiten. */
export function readableReason(reason: string): string {
  return reason.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z/g, (iso) => untilText(iso));
}

/** Kennzeichnung des Zugangs am Lauf; `none` (Mock) bekommt keine. */
export const AUTH_MODE_LABEL: Record<AuthMode, string | null> = {
  none: null,
  subscription: "Abo-Modus",
  api_key: "API-Modus",
};

export const QUOTA_LABEL: Record<QuotaLimitKind, string> = {
  session: "Abo-Sitzungslimit",
  weekly: "Abo-Wochenlimit",
  model: "Abo-Modelllimit",
  extra_usage: "Abo-Limit (Zusatznutzung gesperrt)",
  unknown: "Anbieterlimit",
};

/** Tokenzahl gut lesbar: bis 9.999 genau, darüber gerundet (z. B. „12.345“, „1,2 Mio.“). */
export function tokens(value: number): string {
  if (value < 10_000) return value.toLocaleString("de-DE");
  return new Intl.NumberFormat("de-DE", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

export function usd(value: number): string {
  return `$ ${value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function shortHash(hash: string | null | undefined): string {
  if (!hash) return "keiner";
  return hash.replace("sha256:", "").slice(0, 7);
}

export const STATE_LABEL: Record<WorkOrderState, string> = {
  DRAFT: "Briefing",
  SPEC_REVIEW: "Spec-Freigabe",
  PLAN_REVIEW: "Planung",
  READY: "Startbereit",
  RUNNING: "Läuft",
  VERIFYING: "Prüfung",
  REVIEW_READY: "Review",
  ACCEPTED: "Angenommen",
  STAGING: "Prüfung vor dem Mergen",
  RELEASE_APPROVAL: "Auslieferung",
  RELEASED: "Ausgeliefert",
  WAITING_HUMAN: "Wartet auf dich",
  BLOCKED: "Blockiert",
  PAUSED_PROVIDER: "Provider pausiert",
  SUSPECT: "Unklar",
  FAILED: "Fehlgeschlagen",
  CANCELLED: "Abgebrochen",
};

/**
 * Angezeigter Stand eines Auftrags. „Läuft“ nur, wenn ein Lauf aktiv ist; ein Auftrag in RUNNING ohne
 * aktiven Lauf ist angehalten, wartet auf einen freien Platz oder hat keinen Lauf.
 */
export function orderStatus(
  w: ActivityFacts & { openOwnerSteps?: number; awaitingRolloutSha?: string | null },
): { label: string; cls: string } {
  // Gemergt heißt noch nicht fertig, solange ein Schritt bei dir liegt (etwa ein Skript auf deinem PC).
  if (w.state === "RELEASED" && (w.openOwnerSteps ?? 0) > 0) {
    return { label: "Im Repo, wartet auf dich", cls: "waiting_human" };
  }
  // szyOS selbst: gemergt, aber erst nach dem Ausrollen des Büros sichtbar.
  if (w.state === "RELEASE_APPROVAL" && w.awaitingRolloutSha) {
    return { label: "Gemergt, noch nicht ausgerollt", cls: "waiting_human" };
  }
  if (w.state !== "RUNNING") return { label: STATE_LABEL[w.state], cls: w.state.toLowerCase() };
  switch (orderActivity(w)) {
    case "working":
      return { label: "Läuft", cls: "running" };
    case "held":
      return { label: "Angehalten", cls: "held" };
    case "queued":
      return { label: "Wartet auf freien Platz", cls: "queued" };
    default:
      return { label: "Kein Lauf aktiv", cls: "queued" };
  }
}

export const LANE_ICON: Record<Lane, string> = {
  research: "book-open",
  design: "palette",
  code: "code",
  test: "flask",
  docs: "file",
};

export type TimelineCategory = "plan" | "build" | "test" | "release" | "other";

export interface Described {
  text: string;
  category: TimelineCategory;
  tone: "violet" | "lime" | "cyan" | "orange" | "coral" | "amber" | "lavender" | "red";
  icon: string;
  who: string;
  important: boolean;
}

const payloadText = (e: DomainEvent, key: string): string => String(e.payload[key] ?? "");

/** Wer geprüft hat: echte GitHub-Checks oder die simulierte Mock-CI (ADR-010). */
const checker = (e: DomainEvent): string =>
  e.actor.id === "github-checks"
    ? "GitHub-Checks"
    : e.actor.id === "ohne-aenderung"
      ? "szyOS"
      : "Mock-CI (simuliert)";

/** Übersetzt Domänen-Ereignisse in kurze deutsche Texte für Timeline und Aktivität. */
export function describe(e: DomainEvent): Described | null {
  const title = payloadText(e, "title");
  switch (e.eventType) {
    case "work_order.created":
      return {
        text: "Auftrag angelegt",
        category: "plan",
        tone: "violet",
        icon: "file",
        who: "szyOS",
        important: true,
      };
    case "integration.taskflow_received": {
      const by = payloadText(e, "requestedBy");
      return {
        text: by ? `Aus taskFLOW übernommen, gesendet von ${by}` : "Aus taskFLOW übernommen",
        category: "plan",
        tone: "cyan",
        icon: "file",
        who: "taskFLOW",
        important: true,
      };
    }
    case "spec.proposed":
      return {
        text: `Spec v${payloadText(e, "revision")} vorgelegt`,
        category: "plan",
        tone: "violet",
        icon: "file",
        who: "Planer",
        important: false,
      };
    case "spec.approved":
      return {
        text: "Spec freigegeben",
        category: "plan",
        tone: "lime",
        icon: "check",
        who: "Owner",
        important: true,
      };
    case "plan.proposed":
      return {
        text: `Plan v${payloadText(e, "revision")} vorgelegt`,
        category: "plan",
        tone: "violet",
        icon: "network",
        who: "Orchestrator",
        important: false,
      };
    case "plan.approved":
      return {
        text: `Plan v${payloadText(e, "revision")} freigegeben`,
        category: "plan",
        tone: "lime",
        icon: "check",
        who: "Owner",
        important: true,
      };
    case "run.queued":
      return {
        text: `${title} eingeplant`,
        category: "build",
        tone: "lavender",
        icon: "clock",
        who: "Dispatcher",
        important: false,
      };
    case "run.started":
      return {
        text: "Lauf gestartet",
        category: "build",
        tone: "violet",
        icon: "activity",
        who: agentName(e),
        important: false,
      };
    case "run.pr_opened":
      return {
        text: `Pull Request geöffnet (${payloadText(e, "files")} Dateien, ${payloadText(e, "branch")})`,
        category: "build",
        tone: "cyan",
        icon: "pr",
        who: "Git-Broker",
        important: true,
      };
    case "security.changeset_rejected":
      return {
        text: `Sicherheit: ${payloadText(e, "message")}`,
        category: "build",
        tone: "red",
        icon: "shield",
        who: "Git-Broker",
        important: true,
      };
    case "security.changeset_trimmed":
      return {
        text: payloadText(e, "message"),
        category: "build",
        tone: "amber",
        icon: "shield",
        who: "Git-Broker",
        important: true,
      };
    case "security.egress_blocked":
      return {
        text: `Sicherheit: ${payloadText(e, "message")}`,
        category: "build",
        tone: "red",
        icon: "shield",
        who: agentName(e),
        important: true,
      };
    case "run.log_excerpt":
      return {
        text: payloadText(e, "message"),
        category: "build",
        tone: "lavender",
        icon: "activity",
        who: agentName(e),
        important: false,
      };
    case "run.completed":
      return {
        text: payloadText(e, "summary") || `${title} geliefert`,
        category: "build",
        tone: "cyan",
        icon: "code",
        who: agentName(e),
        important: false,
      };
    case "run.failed":
      return {
        text: `Fehlgeschlagen: ${payloadText(e, "reason")}`,
        category: "build",
        tone: "red",
        icon: "alert",
        who: agentName(e),
        important: true,
      };
    case "run.cancelled":
      return {
        text: "Lauf abgebrochen",
        category: "build",
        tone: "red",
        icon: "x",
        who: "szyOS",
        important: false,
      };
    case "run.tool_approval_requested":
      return {
        text: `Rückfrage: ${payloadText(e, "question")}`,
        category: "build",
        tone: "amber",
        icon: "inbox",
        who: agentName(e),
        important: true,
      };
    case "run.quota_exhausted": {
      const limit = QUOTA_LABEL[payloadText(e, "limit") as QuotaLimitKind] ?? QUOTA_LABEL.unknown;
      const until = payloadText(e, "pausedUntil");
      return {
        text: `${limit} erreicht${until ? `, weiter ab ${untilText(until)}` : ""}`,
        category: "build",
        tone: "amber",
        icon: "clock",
        who: agentName(e),
        important: true,
      };
    }
    case "ci.check_started":
      return {
        text: `Prüfung: ${title}`,
        category: "test",
        tone: "orange",
        icon: "shield",
        who: checker(e),
        important: false,
      };
    case "workitem.verified":
      return {
        text:
          e.actor.id === "github-checks"
            ? `${title} durch GitHub-Checks belegt`
            : e.actor.id === "ohne-aenderung"
              ? `${title}: ohne Dateiänderung, nichts für die CI`
              : `${title} simuliert geprüft`,
        category: "test",
        tone: "lime",
        icon: "shield",
        who: checker(e),
        important: true,
      };
    case "ci.check_completed":
      return {
        text: e.payload.mock ? "Quality Gate bestanden (simuliert)" : "Quality Gate bestanden",
        category: "test",
        tone: "lime",
        icon: "shield",
        who: checker(e),
        important: true,
      };
    case "ci.check_failed":
    case "ci.no_checks":
      return {
        text: payloadText(e, "message"),
        category: "test",
        tone: "red",
        icon: "alert",
        who: checker(e),
        important: true,
      };
    case "review.ready":
      return {
        text: "Review bereit",
        category: "test",
        tone: "amber",
        icon: "pr",
        who: "szyOS",
        important: true,
      };
    case "staging.smoke_passed":
      return {
        text: "Staging (simuliert, alter Ablauf)",
        category: "release",
        tone: "lime",
        icon: "server",
        who: "Mock-Staging",
        important: true,
      };
    case "decision.requested":
      return {
        text: payloadText(e, "question"),
        category: "other",
        tone: "amber",
        icon: "inbox",
        who: "szyOS",
        important: true,
      };
    case "decision.resolved":
      return {
        text: `Entschieden: ${payloadText(e, "optionTitle")}`,
        category: "other",
        tone: "lime",
        icon: "check",
        who: "Mensch",
        important: true,
      };
    case "release.deployed":
      return {
        text: "Ausgeliefert (simuliert, alter Ablauf)",
        category: "release",
        tone: "lime",
        icon: "rocket",
        who: "Mock-Release-Broker",
        important: true,
      };
    case "release.checked": {
      const p = e.payload as {
        ready?: boolean;
        alreadyMerged?: boolean;
        route?: string;
        blockers?: string[];
      };
      return {
        text:
          p.route === "none"
            ? "Kein Repo: nichts auszuliefern"
            : p.alreadyMerged
              ? "Vor dem Mergen geprüft: schon gemergt"
              : p.ready
                ? "Vor dem Mergen geprüft: bereit"
                : `Vor dem Mergen geprüft: ${p.blockers?.[0] ?? "noch nicht bereit"}`,
        category: "release",
        tone: p.ready || p.alreadyMerged || p.route === "none" ? "lime" : "amber",
        icon: "pr",
        who: p.route === "none" ? "szyOS" : "GitHub-Checks",
        important: true,
      };
    }
    case "release.merged":
      return {
        text: `Pull Request #${payloadText(e, "prNumber")} gemergt`,
        category: "release",
        tone: "lime",
        icon: "git",
        who: "Git-Broker",
        important: true,
      };
    case "release.completed": {
      const route = payloadText(e, "route");
      return {
        text:
          route === "none"
            ? "Als erledigt markiert"
            : route === "manual"
              ? "Von Hand ausgeliefert, Checkliste abgehakt"
              : "Ausgeliefert",
        category: "release",
        tone: "lime",
        icon: "rocket",
        who: "Mensch",
        important: true,
      };
    }
    case "work_order.precheck_done":
      return {
        text: "Vorprüfung: laut Repo schon umgesetzt",
        category: "plan",
        tone: "amber",
        icon: "search",
        who: "szyOS",
        important: true,
      };
    case "work_order.already_done":
      return {
        text: "Als schon umgesetzt abgeschlossen",
        category: "release",
        tone: "lime",
        icon: "check",
        who: "Mensch",
        important: true,
      };
    case "work_order.state_changed": {
      const to = payloadText(e, "to") as WorkOrderState;
      if (to === "RELEASE_APPROVAL") {
        return {
          text: "Auslieferung wartet auf dich",
          category: "release",
          tone: "amber",
          icon: "rocket",
          who: "szyOS",
          important: true,
        };
      }
      if (to === "RUNNING" && payloadText(e, "from") === "READY") {
        return {
          text: "Umsetzung gestartet",
          category: "build",
          tone: "violet",
          icon: "activity",
          who: "Owner",
          important: true,
        };
      }
      if (to === "RUNNING" && payloadText(e, "from") === "PAUSED_PROVIDER") {
        return {
          text: "Nach Anbieterpause fortgesetzt",
          category: "build",
          tone: "violet",
          icon: "activity",
          who: "szyOS",
          important: true,
        };
      }
      if (to === "BLOCKED") {
        return {
          text: `Blockiert: ${readableReason(payloadText(e, "reason"))}`,
          category: "other",
          tone: "red",
          icon: "alert",
          who: "szyOS",
          important: true,
        };
      }
      return null;
    }
    case "killswitch.triggered":
      return {
        text: "Kill Switch ausgelöst",
        category: "other",
        tone: "red",
        icon: "power",
        who: "Owner",
        important: true,
      };
    case "provider_auth.changed": {
      const onOff = (key: string) => (e.payload[key] === true ? "an" : "aus");
      const cancelled = Number(e.payload.cancelledRuns ?? 0);
      return {
        text: `Zugang geändert: Abo-Modus ${onOff("subscription")}, API-Reserve ${onOff("apiKey")}${cancelled > 0 ? `, ${cancelled} ${cancelled === 1 ? "Lauf" : "Läufe"} beendet` : ""}`,
        category: "other",
        tone: "violet",
        icon: "key",
        who: "Owner",
        important: true,
      };
    }
    default:
      return null;
  }
}

function agentName(e: DomainEvent): string {
  const lane = e.payload.lane as Lane | undefined;
  const role =
    lane === "research"
      ? "Research-Agent"
      : lane === "design"
        ? "Design-Agent"
        : lane === "test"
          ? "Test-Agent"
          : lane === "docs"
            ? "Doku-Agent"
            : "Builder";
  return role;
}

export function adapterLabel(adapter: string | undefined): string {
  if (!adapter) return "";
  if (adapter === "mock") return "Mock";
  if (adapter === "claude-code") return "Claude Code";
  if (adapter === "codex") return "Codex";
  if (adapter === "gemini") return "Gemini CLI";
  return adapter;
}
